// ============================================================================
// SUBSCRIPTION PLANS CONSTANTS
// ============================================================================

/**
 * Subscription plan interface matching backend structure
 */
export interface SubscriptionPlan {
  readonly _id: string;
  readonly plan_name: string;
  readonly subscription_type: string;
  readonly prefix: string;
  readonly entity_types: readonly string[];
  readonly available_platforms?: readonly string[];
  readonly max_platforms?: number;
  readonly pricing_matrix?: Record<string, number>;
  readonly tier:
    | "starter"
    | "basic"
    | "plus"
    | "premium"
    | "enterprise"
    | "usage";
  readonly base_price: number;
  readonly default_quota?: number;
  readonly calls_per_min: number;
  readonly influencer_channels?: number | null;
  readonly pulse_updates?: number | null;
  readonly is_demo: boolean;
  readonly active: boolean;
  readonly date_created: string;
  readonly created_by: string | null;
}

/**
 * Centralized subscription plans configuration
 * Single source of truth for all plan metadata
 */
export const SUBSCRIPTION_PLANS: Record<string, SubscriptionPlan> = {
  // ============================================================================
  // SOURCE TIER
  // ============================================================================
  "69730f65385ceb78ddd72b87": {
    _id: "69730f65385ceb78ddd72b87",
    plan_name: "Source - Usage Based",
    subscription_type: "source_monitoring",
    prefix: "source_usage",
    entity_types: ["source"],
    tier: "usage",
    base_price: 100,
    calls_per_min: 100,
    is_demo: false,
    active: true,
    date_created: "2026-02-24T01:03:33.883000",
    created_by: null,
  },

  // ============================================================================
  // STARTER TIER
  // ============================================================================
  "68f97ee7345ef38678391600": {
    _id: "68f97ee7345ef38678391600",
    plan_name: "Starter - Influencer Monitoring",
    subscription_type: "influencer_monitoring",
    prefix: "inf_starter",
    entity_types: ["influencers"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 1,
    pricing_matrix: {
      "1": 762,
      "2": 1275,
      "3": 1790,
      "4": 2302,
      "5": 2645,
      "6": 3158,
    },
    tier: "starter",
    base_price: 762,
    default_quota: 1000,
    calls_per_min: 100,
    influencer_channels: 1000,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:33.883000",
    created_by: null,
  },
  "68f97f0636130d4b376a2f83": {
    _id: "68f97f0636130d4b376a2f83",
    plan_name: "Starter - Keyword Monitoring",
    subscription_type: "keyword_monitoring",
    prefix: "starter",
    entity_types: [
      "social_media",
      "influencers",
      "quadmedia",
      "posts",
      "articles",
    ],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 1,
    pricing_matrix: {
      "1": 676,
      "2": 1275,
      "3": 1874,
      "4": 2474,
      "5": 3073,
      "6": 3586,
    },
    tier: "starter",
    base_price: 762,
    default_quota: 13000,
    calls_per_min: 100,
    influencer_channels: null,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:04:05.015000",
    created_by: null,
  },
  "68f97eec345ef38678391605": {
    _id: "68f97eec345ef38678391605",
    plan_name: "Starter - Pulse",
    subscription_type: "pulse_monitoring",
    prefix: "pulse_starter",
    entity_types: ["pulse"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 1,
    pricing_matrix: {
      "1": 419,
      "2": 590,
      "3": 762,
      "4": 933,
      "5": 1018,
      "6": 1104,
    },
    tier: "starter",
    base_price: 419,
    default_quota: 25000,
    calls_per_min: 100,
    influencer_channels: null,
    pulse_updates: 25000,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:40.307000",
    created_by: null,
  },

  // ============================================================================
  // BASIC TIER
  // ============================================================================
  "68f97eea345ef38678391601": {
    _id: "68f97eea345ef38678391601",
    plan_name: "Basic - Influencer Monitoring",
    subscription_type: "influencer_monitoring",
    prefix: "inf_basic",
    entity_types: ["influencers"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 2,
    pricing_matrix: {
      "1": 1532,
      "2": 2474,
      "3": 3415,
      "4": 4358,
      "5": 5042,
      "6": 5983,
    },
    tier: "basic",
    base_price: 1532,
    default_quota: 5000,
    calls_per_min: 100,
    influencer_channels: 5000,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:36.939000",
    created_by: null,
  },
  "68f97f0836130d4b376a2f84": {
    _id: "68f97f0836130d4b376a2f84",
    plan_name: "Basic - Keyword Monitoring",
    subscription_type: "keyword_monitoring",
    prefix: "basic",
    entity_types: [
      "social_media",
      "influencers",
      "quadmedia",
      "posts",
      "articles",
    ],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 2,
    pricing_matrix: {
      "1": 1361,
      "2": 2730,
      "3": 4100,
      "4": 5470,
      "5": 6839,
      "6": 7866,
    },
    tier: "basic",
    base_price: 1532,
    default_quota: 500000,
    calls_per_min: 100,
    influencer_channels: null,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:04:08.019000",
    created_by: null,
  },
  "68f97eed345ef38678391606": {
    _id: "68f97eed345ef38678391606",
    plan_name: "Basic - Pulse",
    subscription_type: "pulse_monitoring",
    prefix: "pulse_basic",
    entity_types: ["pulse"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 2,
    pricing_matrix: {
      "1": 676,
      "2": 934,
      "3": 1190,
      "4": 1446,
      "5": 1584,
      "6": 1721,
    },
    tier: "basic",
    base_price: 676,
    default_quota: 50000,
    calls_per_min: 100,
    influencer_channels: null,
    pulse_updates: 50000,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:40.952000",
    created_by: null,
  },

  // ============================================================================
  // PLUS TIER
  // ============================================================================
  "68f97eea345ef38678391602": {
    _id: "68f97eea345ef38678391602",
    plan_name: "Plus - Influencer Monitoring",
    subscription_type: "influencer_monitoring",
    prefix: "inf_plus",
    entity_types: ["influencers"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 3,
    pricing_matrix: {
      "1": 2902,
      "2": 4956,
      "3": 7010,
      "4": 9065,
      "5": 10606,
      "6": 12660,
    },
    tier: "plus",
    base_price: 2902,
    default_quota: 25000,
    calls_per_min: 100,
    influencer_channels: 25000,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:38.448000",
    created_by: null,
  },
  "68f97f0836130d4b376a2f85": {
    _id: "68f97f0836130d4b376a2f85",
    plan_name: "Plus - Keyword Monitoring",
    subscription_type: "keyword_monitoring",
    prefix: "plus",
    entity_types: [
      "social_media",
      "influencers",
      "quadmedia",
      "posts",
      "articles",
    ],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 3,
    pricing_matrix: {
      "1": 2046,
      "2": 4100,
      "3": 6154,
      "4": 8209,
      "5": 10263,
      "6": 11796,
    },
    tier: "plus",
    base_price: 2902,
    default_quota: 1000,
    calls_per_min: 200,
    influencer_channels: null,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:04:08.630000",
    created_by: null,
  },
  "68f97eed345ef38678391607": {
    _id: "68f97eed345ef38678391607",
    plan_name: "Plus - Pulse",
    subscription_type: "pulse_monitoring",
    prefix: "pulse_plus",
    entity_types: ["pulse"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 3,
    pricing_matrix: {
      "1": 1104,
      "2": 1532,
      "3": 1960,
      "4": 2388,
      "5": 2645,
      "6": 2902,
    },
    tier: "plus",
    base_price: 1104,
    default_quota: 100000,
    calls_per_min: 100,
    influencer_channels: null,
    pulse_updates: 100000,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:41.559000",
    created_by: null,
  },

  // ============================================================================
  // PREMIUM TIER
  // ============================================================================
  "68f97eeb345ef38678391603": {
    _id: "68f97eeb345ef38678391603",
    plan_name: "Premium - Influencer Monitoring",
    subscription_type: "influencer_monitoring",
    prefix: "inf_premium",
    entity_types: ["influencers"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 4,
    pricing_matrix: {
      "1": 4442,
      "2": 7695,
      "3": 10941,
      "4": 14201,
      "5": 16598,
      "6": 19850,
    },
    tier: "premium",
    base_price: 4442,
    default_quota: 50000,
    calls_per_min: 100,
    influencer_channels: 50000,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:39.057000",
    created_by: null,
  },
  "68f97f0936130d4b376a2f86": {
    _id: "68f97f0936130d4b376a2f86",
    plan_name: "Premium - Keyword Monitoring",
    subscription_type: "keyword_monitoring",
    prefix: "premium",
    entity_types: [
      "social_media",
      "influencers",
      "quadmedia",
      "posts",
      "articles",
    ],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 4,
    pricing_matrix: {
      "1": 4015,
      "2": 8038,
      "3": 12061,
      "4": 16068,
      "5": 20107,
      "6": 23606,
    },
    tier: "premium",
    base_price: 4442,
    default_quota: 3000,
    calls_per_min: 300,
    influencer_channels: null,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:04:09.264000",
    created_by: null,
  },
  "68f97eee345ef38678391608": {
    _id: "68f97eee345ef38678391608",
    plan_name: "Premium - Pulse",
    subscription_type: "pulse_monitoring",
    prefix: "pulse_premium",
    entity_types: ["pulse"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 4,
    pricing_matrix: {
      "1": 2987,
      "2": 4357,
      "3": 5726,
      "4": 7096,
      "5": 7781,
      "6": 8466,
    },
    tier: "premium",
    base_price: 2987,
    default_quota: 500000,
    calls_per_min: 100,
    influencer_channels: null,
    pulse_updates: 500000,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:42.170000",
    created_by: null,
  },

  // ============================================================================
  // ENTERPRISE TIER
  // ============================================================================
  "68f97eeb345ef38678391604": {
    _id: "68f97eeb345ef38678391604",
    plan_name: "Enterprise - Influencer Monitoring",
    subscription_type: "influencer_monitoring",
    prefix: "inf_enterprise",
    entity_types: ["influencers"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 6,
    pricing_matrix: {
      "1": 7267,
      "2": 12489,
      "3": 17710,
      "4": 22932,
      "5": 26784,
      "6": 31206,
    },
    tier: "enterprise",
    base_price: 7267,
    default_quota: 100000,
    calls_per_min: 100,
    influencer_channels: 100000,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:39.665000",
    created_by: null,
  },
  "68f97f0a36130d4b376a2f87": {
    _id: "68f97f0a36130d4b376a2f87",
    plan_name: "Enterprise - Keyword Monitoring",
    subscription_type: "keyword_monitoring",
    prefix: "km_enterprise",
    entity_types: ["social_media", "influencers", "posts", "articles"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 6,
    pricing_matrix: {
      "1": 5837,
      "2": 11470,
      "3": 17111,
      "4": 22753,
      "5": 28410,
      "6": 33025,
    },
    tier: "enterprise",
    base_price: 7267,
    default_quota: 10000,
    calls_per_min: 500,
    influencer_channels: null,
    pulse_updates: null,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:04:09.913000",
    created_by: null,
  },
  "68f97eef345ef38678391609": {
    _id: "68f97eef345ef38678391609",
    plan_name: "Enterprise - Pulse",
    subscription_type: "pulse_monitoring",
    prefix: "pulse_enterprise",
    entity_types: ["pulse"],
    available_platforms: [
      "facebook",
      "youtube",
      "twitter",
      "tiktok",
      "reddit",
      "instagram",
    ],
    max_platforms: 6,
    pricing_matrix: {
      "1": 4871,
      "2": 7096,
      "3": 9322,
      "4": 11555,
      "5": 12746,
      "6": 13944,
    },
    tier: "enterprise",
    base_price: 4871,
    default_quota: 1000000,
    calls_per_min: 100,
    influencer_channels: null,
    pulse_updates: 1000000,
    is_demo: false,
    active: true,
    date_created: "2025-10-23T01:03:42.782000",
    created_by: null,
  },
} as const;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get plan by ID with runtime validation
 * @param planId - The plan ID to look up
 * @returns The subscription plan or undefined if not found
 */
export function getPlanById(planId: string): SubscriptionPlan | undefined {
  return SUBSCRIPTION_PLANS[planId];
}

/**
 * Get multiple plans by their IDs
 * @param planIds - Array of plan IDs
 * @returns Array of subscription plans that were found
 */
export function getPlansByIds(planIds: string[]): SubscriptionPlan[] {
  return planIds
    .map((id) => SUBSCRIPTION_PLANS[id])
    .filter((plan): plan is SubscriptionPlan => plan !== undefined);
}

/**
 * Type guard to validate if a plan ID exists
 * @param planId - The plan ID to validate
 * @returns True if the plan exists
 */
export function isValidPlanId(planId: string): boolean {
  return planId in SUBSCRIPTION_PLANS;
}

/**
 * Get all plans for a specific subscription type
 * @param subscriptionType - The subscription type to filter by
 * @returns Array of plans matching the subscription type
 */
export function getPlansByType(subscriptionType: string): SubscriptionPlan[] {
  return Object.values(SUBSCRIPTION_PLANS).filter(
    (plan) => plan.subscription_type === subscriptionType,
  );
}

/**
 * Get all plans for a specific tier
 * @param tier - The tier to filter by
 * @returns Array of plans matching the tier
 */
export function getPlansByTier(
  tier: "starter" | "basic" | "plus" | "premium" | "enterprise",
): SubscriptionPlan[] {
  return Object.values(SUBSCRIPTION_PLANS).filter((plan) => plan.tier === tier);
}

/**
 * Get all active plans
 * @returns Array of all active plans
 */
export function getActivePlans(): SubscriptionPlan[] {
  return Object.values(SUBSCRIPTION_PLANS).filter((plan) => plan.active);
}

// ============================================================================
// CLIENT ADD (ADMIN)
// ============================================================================

/**
 * Default selected platforms for new client subscriptions.
 * All plans support: twitter, facebook, reddit, tiktok, youtube,.
 */
export const CLIENT_ADD_DEFAULT_PLATFORMS = [
  "twitter",
  "facebook",
  "reddit",
  "tiktok",
  "instagram",
  "youtube",
] as const;

/**
 * Map monitoring tab to subscription_type in SUBSCRIPTION_PLANS
 */
export const MONITORING_TO_SUBSCRIPTION_TYPE: Record<string, string> = {
  keyword: "keyword_monitoring",
  influencer: "influencer_monitoring",
  source: "source_monitoring",
  pulse: "pulse_monitoring",
};
